// The Studio's "How it lands in …" panel, bound to the page shown
// (HowItLands.tsx; useHowItLands.ts asks and runs the intents). Projects
// the server's answer onto the open page: its rows and proposals, and the
// margin marks its pages read (landingMarks.tsx) while the panel is open.

export { landingPageOf, StudioLandingPanel, studioLandingMarks };

import { exhaustSwitchCase } from "@bilda/utils";
import type { ReactNode } from "react";
import type { StudioCard } from "../../../components/Studio/studioCards.js";
import type { studioPage } from "../../../components/StudioEditor/studioPages.js";

type StudioPage = ReturnType<typeof studioPage>;

import { HowItLands } from "../../../components/StudioLanding/HowItLands.js";
import type { LandingMarks } from "../../../components/StudioLanding/landingMarks.js";
import {
    type LandingView,
    landingViewOf,
    proposalsFor,
} from "../../../components/StudioLanding/landingView.js";
import type { LandingFormat } from "../../ports/landing.js";
import type { HowItLandsPanel } from "./useHowItLands.js";

// The page shown, as the panel needs it: its id, what it is, its parts.
type LandingPage = {
    readonly id: string;
    readonly kind: "course" | "module" | "page";
    readonly parts: readonly string[];
};

const landingPageOf = (page: StudioPage): LandingPage | undefined => {
    switch (page.type) {
        case "container":
            return {
                id: page.id,
                kind: page.container.kind,
                parts: page.container.parts.map((p) => p.id),
            };
        case "root":
            return page.container === undefined
                ? undefined
                : {
                      id: page.container.id,
                      kind: page.container.kind,
                      parts: page.container.parts.map((p) => p.id),
                  };
        case "document":
            return { id: page.id, kind: "page", parts: [] };
        case "about":
            return undefined;
        default:
            return exhaustSwitchCase(page);
    }
};

const titlesOf = (cards: readonly StudioCard[]): ReadonlyMap<string, string> =>
    new Map(
        cards.flatMap((c): readonly (readonly [string, string])[] => [
            [c.id, c.title],
            ...(c.type === "container"
                ? c.parts.flatMap(
                      (p): readonly (readonly [string, string])[] => [
                          [p.id, p.title],
                          ...p.parts.map((q): readonly [string, string] => [
                              q.id,
                              q.title,
                          ]),
                      ],
                  )
                : []),
        ]),
    );

const viewOf = (
    panel: HowItLandsPanel,
    page: LandingPage | undefined,
    cards: readonly StudioCard[],
): LandingView | undefined =>
    panel.answer?.type === "landed" && page !== undefined
        ? landingViewOf(
              panel.answer.landing,
              page.id,
              page.parts,
              titlesOf(cards),
          )
        : undefined;

// The marks the open page reads while the panel is open (else none).
const studioLandingMarks = (
    panel: HowItLandsPanel,
    page: LandingPage | undefined,
    cards: readonly StudioCard[],
): LandingMarks | null => {
    const view = panel.open ? viewOf(panel, page, cards) : undefined;
    return view === undefined
        ? null
        : {
              blocks: view.blocks,
              parts: view.parts,
              pointed: panel.pointed,
              onPoint: panel.onPoint,
          };
};

const StudioLandingPanel = ({
    panel,
    format,
    page,
    cards,
    online,
    onExport,
}: {
    readonly panel: HowItLandsPanel;
    readonly format: LandingFormat;
    readonly page: LandingPage | undefined;
    readonly cards: readonly StudioCard[];
    readonly online: boolean;
    readonly onExport: () => void;
}): ReactNode => (
    <HowItLands
        format={format}
        page={page?.kind ?? "page"}
        answer={panel.answer ?? { type: "waiting" }}
        view={viewOf(panel, page, cards)}
        proposals={
            panel.answer?.type === "landed" && page !== undefined
                ? proposalsFor(panel.answer.landing, page.id)
                : []
        }
        online={online}
        moving={panel.moving}
        onClose={panel.onClose}
        onRetry={panel.onRetry}
        onExport={onExport}
        onPoint={panel.onPoint}
        onShowMe={panel.onShowMe}
        onMoveIt={panel.onMoveIt}
    />
);
